import { DOCUMENT } from '@angular/common';
import { Injectable, InjectionToken, inject } from '@angular/core';
import { IdToken } from '@auth0/auth0-angular';
import { map } from 'rxjs';

import {
  AppMetadata,
  Portal,
  PortalItem,
  mockPortals,
} from '@digital-platform/aura-apps/domain';
import { Auth0Service } from '@digital-platform/aura-authentication/services';
import { Environment } from '@digital-platform/rs-shared/domain';
import { ENVIRONMENT } from '@digital-platform/rs-shared/services';
import { FeatureFlagService } from '@digital-platform/util-feature-flags/services';

export const WEB_COMPONENT = new InjectionToken<boolean>('WEB_COMPONENT');

@Injectable({
  providedIn: 'root',
})
export class AppsService {
  private auth0Service = inject(Auth0Service);
  private document = inject(DOCUMENT);
  private environment: Environment = inject(ENVIRONMENT);
  private isWebComponent = inject(WEB_COMPONENT, { optional: true });
  private featureFlagService = inject(FeatureFlagService);
  private apps: Portal[] = [];
  private currentApp = '';
  constructor() {
    this.apps = Object.entries(mockPortals).map(([key, value]) =>
      this.getPortalDetails(key, value)
    );
  }

  public activeApps$ = this.auth0Service
    .getIdTokenClaims()
    .pipe(map((user) => this.getActivePortalsForUser(user)));

  public availableApps$ = this.auth0Service
    .getIdTokenClaims()
    .pipe(map((user) => this.getAvailablePortalsForUser(user)));

  public setCurrentApp(app: string): void {
    this.currentApp = app;
  }

  public getCurrentApp(): Portal | undefined {
    const location = this.document.location;

    // use the current app flag or the current URL to attempt to determine the active app
    let app: Portal | undefined;
    if (this.currentApp) {
      app = this.apps.find((a) => a.id === this.currentApp);
    } else {
      app = this.apps.find(
        (a) =>
          a.dashboardLink &&
          (location.href.startsWith(a.dashboardLink) ||
            (location.pathname.length > 1 &&
              a.dashboardLink.endsWith(location.pathname)))
      );
    }

    console.log({
      location,
      app,
      currentApp: this.currentApp,
    });

    if (app) {
      return {
        ...app,
        signUpLink: this.getAppLink(app.signUpLink || ''),
        dashboardLink: this.getAppLink(app.dashboardLink || ''),
      };
    }

    return app;
  }

  public getRootApp(): Portal | undefined {
    const app = this.apps.find((a) => a.id === 'APPS');

    if (app) {
      return {
        ...app,
        signUpLink: this.getAppLink(app.signUpLink || ''),
        dashboardLink: this.getAppLink(app.dashboardLink || ''),
      };
    }

    return app;
  }

  public getActivePortalsForUser(
    user: IdToken | null | undefined
  ): PortalItem[] {
    if (!user) {
      return [];
    }

    return Object.entries(mockPortals)
      .filter(([key]) => this.hasAccessToPortal(user, key.toUpperCase()))
      .map(([key, value]) => this.getPortalItem(user, key, value));
  }

  public getAvailablePortalsForUser(
    user: IdToken | null | undefined
  ): PortalItem[] {
    if (!user) {
      return [];
    }

    return Object.entries(mockPortals)
      .filter(([key]) => !this.hasAccessToPortal(user, key.toUpperCase()))
      .filter(([key]) => key !== 'NA') // No Signup for NA portal - remove from list
      .map(([key, value]) => this.getPortalItem(user, key, value));
  }

  public hasAccessToPortal(user: IdToken, portal: string): boolean {
    const { cdh_id, cdh_contact_profile_id } = this.getAppMetadata(user);
    let { additional_portals } = this.getAppMetadata(user);
    additional_portals =
      additional_portals?.map((name: string) => name.toUpperCase()) || [];
    const approvalStatuses = this.getCorPortalsApprovalStatus(user);

    switch (portal) {
      case 'APPS':
      case 'PROFILE':
        return true; // Always allow access to these portals
      case 'ONLINE':
        return !!cdh_id && !!cdh_contact_profile_id; // If these fields are present, the user has access
      case 'COR1':
      case 'COR2': {
        // If the user has an approval status and the portal is in the additional_portals list, the user has access
        return (
          ['approved', 'pending', 'denied'].includes(
            approvalStatuses.get(portal) as string
          ) && additional_portals.includes(portal)
        );
      }
      case 'SW':
      case 'NA':
        return additional_portals.includes(portal); // If these portals exists in the additional_portals list, the user has access
      default:
        return false; // Prevent access to any other portals just in case
    }
  }

  private getAppMetadata(user: IdToken): AppMetadata {
    return (
      user['https://republicservices.com/app_metadata'] ||
      user['app_metadata'] ||
      {}
    );
  }

  // Returns a map of approval statuses for COR1 and COR2 portals
  /**
   * Retrieves the approval status of COR portals.
   * @returns A map containing the approval status of each COR portal.
   */

  private getCorPortalsApprovalStatus(
    user: IdToken
  ): Map<string, PortalItem['status']> {
    const approvalStatuses = this.getAppMetadata(user)['approval_statuses'];
    if (!approvalStatuses) {
      return new Map();
    }
    return new Map(
      approvalStatuses.map(
        (approval: { application_name: string; status: string }) => [
          approval.application_name.toUpperCase(),
          approval.status.toLowerCase() as
            | 'approved'
            | 'pending'
            | 'denied'
            | 'default',
        ]
      )
    );
  }

  private getPortalDetails(key: string, portal: Portal): Portal {
    const { apps, rscom, cor1, cor2, sw, na, profile } =
      this.environment.appSwitcherPortals || {};

    const appSwitcherRootUrl =
      this.environment.appSwitcherRootUrl || 'https://www.republicservices.com';

    switch (key) {
      case 'APPS':
        return {
          ...portal,
          icon: 'home',
          dashboardLink:
            apps?.dashboard || `${appSwitcherRootUrl}/app-switcher`,
        };
      case 'ONLINE':
        return {
          ...portal,
          dashboardLink:
            rscom?.dashboard || `${appSwitcherRootUrl}/account/dashboard`,
          signUpLink: rscom?.signUp || '',
        };
      case 'COR1':
        return {
          ...portal,
          dashboardLink: cor1?.dashboard || '',
          signUpLink: cor1?.signUp || '',
        };
      case 'COR2':
        return {
          ...portal,
          dashboardLink: cor2?.dashboard || '',
          signUpLink: cor2?.signUp || '',
        };
      case 'SW':
        return {
          ...portal,
          dashboardLink: sw?.dashboard || '',
          signUpLink: sw?.signUp || '',
        };
      case 'NA':
        return {
          ...portal,
          dashboardLink: na?.dashboard || '',
          signUpLink: na?.signUp || '',
        };
      case 'PROFILE':
        return {
          ...portal,
          dashboardLink: profile?.dashboard || '',
        };
    }

    return portal;
  }

  private getAppLink(link: string): string {
    const appSwitcherRootUrl =
      this.environment.appSwitcherRootUrl || 'https://www.republicservices.com';

    if (
      this.isWebComponent &&
      link.indexOf('http://') !== 0 &&
      link.indexOf('https://') !== 0
    ) {
      link = `${appSwitcherRootUrl}${link}`;
    }

    return link;
  }

  private getPortalItem(
    user: IdToken,
    key: string,
    portal: Portal
  ): PortalItem {
    const approvalStatuses = this.getCorPortalsApprovalStatus(user);
    const { apps, rscom, cor1, cor2, sw, na, profile } =
      this.environment.appSwitcherPortals || {};

    const appSwitcherRootUrl =
      this.environment.appSwitcherRootUrl || 'https://www.republicservices.com';

    const name = portal.name;
    const shortName = portal.shortName;
    let link = '';
    let icon = 'dots';
    let message = portal.message;
    let status: PortalItem['status'] = 'default';

    switch (key) {
      case 'APPS':
        status = 'approved';
        icon = 'home';
        link = apps?.dashboard || `${appSwitcherRootUrl}/app-switcher`;
        break;
      case 'ONLINE':
        status = 'approved';
        icon = 'recycle-waste';
        link =
          (this.hasAccessToPortal(user, key)
            ? rscom?.dashboard
            : rscom?.signUp) || '';
        break;
      case 'COR1':
        status = approvalStatuses.get(key) || 'default';
        icon = 'cor';
        link =
          (this.hasAccessToPortal(user, key)
            ? cor1?.dashboard
            : cor1?.signUp) || '';
        break;
      case 'COR2':
        status = approvalStatuses.get(key) || 'default';
        icon = 'cor';
        link =
          (this.hasAccessToPortal(user, key)
            ? cor2?.dashboard
            : cor2?.signUp) || '';
        break;
      case 'SW':
        status = 'approved';
        icon = 'special-waste';
        link =
          (this.hasAccessToPortal(user, key) ? sw?.dashboard : sw?.signUp) ||
          '';
        break;
      case 'NA':
        status = 'approved';
        icon = 'na-portal';
        link =
          (this.hasAccessToPortal(user, key) ? na?.dashboard : na?.signUp) ||
          '';
        break;
      case 'PROFILE':
        status = 'approved';
        icon = 'user';
        link = profile?.dashboard || `${appSwitcherRootUrl}/profile`;
        break;
    }

    if (status === 'pending') {
      message = portal.pendingStateMessage || '';
    } else if (status === 'denied') {
      message = portal.deniedStateMessage || '';
    }

    link = this.getAppLink(link);

    return {
      name,
      shortName,
      link,
      icon,
      message,
      status,
      hiddenInAppSwitcher: portal.hiddenInAppSwitcher,
    };
  }
}
